import { EventEmitter, Injectable } from '@angular/core';

/** Zgody, o które pytamy w banerze. Niezbędnych (sesja) nie da się wyłączyć. */
export interface ConsentState {
  /** Pomiar ruchu - analytics_storage */
  analytics: boolean;
  /** Reklama i konwersje Google Ads - ad_storage, ad_user_data, ad_personalization */
  marketing: boolean;
}

interface StoredConsent extends ConsentState {
  version: number;
  /** Kiedy gość podjął decyzję - RODO każe umieć ją odtworzyć. */
  date: string;
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Zgoda na ciasteczka Google (Consent Mode v2).
 *
 * Podział ról: `src/index.html` ustawia stan WYJŚCIOWY (wszystko odmowne) i
 * odtwarza zapisany wybór, zanim ruszy gtag.js - tamten kod musi wykonać się
 * przed tagiem, więc siedzi w HTML-u, nie tutaj. Ten serwis obsługuje wyłącznie
 * ZMIANĘ zdania: pokazuje baner, gdy wyboru jeszcze nie ma, zapisuje go i
 * wysyła `consent update` do gtag.
 *
 * Wybór trzymamy w localStorage (klucz `cookieConsent`), a nie w ciasteczku -
 * ciasteczko zgody wysyłane przy każdym żądaniu do backendu byłoby zbędnym
 * bagażem, a i tak nikt go po stronie PHP nie czyta.
 *
 * Po dołożeniu nowej kategorii podnieś `VERSION` (i tę samą liczbę w
 * `index.html`) - stary wybór przestaje wtedy wystarczać i baner pyta jeszcze
 * raz, zamiast po cichu przyjmować zgodę na coś, o co nikt nie pytał.
 */
@Injectable()
export class ConsentService {

  static readonly STORAGE_KEY = 'cookieConsent';
  static readonly VERSION = 1;

  /** Baner ma się pokazać (brak wyboru) albo gość otworzył ustawienia ze stopki. */
  readonly visibilityChanged = new EventEmitter<boolean>();

  private stored: StoredConsent | null = null;

  constructor() {
    this.stored = this.read();
  }

  /** Wybór już zapadł - baner nie ma po co wracać sam z siebie. */
  get decided(): boolean {
    return this.stored !== null;
  }

  /** Aktualne ustawienia - dla banera i dla ekranu ustawień prywatności. */
  get state(): ConsentState {
    return {
      analytics: this.stored?.analytics ?? false,
      marketing: this.stored?.marketing ?? false,
    };
  }

  /** Wołane raz przy starcie aplikacji: pytamy tylko tych, którzy nie odpowiedzieli. */
  start(): void {
    if (!this.decided) {
      this.visibilityChanged.emit(true);
    }
  }

  /** Link „Ustawienia prywatności" w stopce - zmiana zdania w każdej chwili. */
  open(): void {
    this.visibilityChanged.emit(true);
  }

  /** Zapisuje wybór, chowa baner i przekazuje zgody do Google. */
  save(state: ConsentState): void {
    this.stored = {
      ...state,
      version: ConsentService.VERSION,
      date: new Date().toISOString(),
    };

    try {
      localStorage.setItem(ConsentService.STORAGE_KEY, JSON.stringify(this.stored));
    } catch {
      // okno prywatne albo zablokowana pamięć witryny - zgoda obowiązuje
      // do końca wizyty, przy następnej zapytamy ponownie
    }

    this.push(state);
    this.visibilityChanged.emit(false);
  }

  acceptAll(): void {
    this.save({ analytics: true, marketing: true });
  }

  rejectAll(): void {
    this.save({ analytics: false, marketing: false });
  }

  /** Zamknięcie ustawień bez zmiany zdania - dozwolone tylko, gdy wybór już jest. */
  dismiss(): void {
    if (this.decided) {
      this.visibilityChanged.emit(false);
    }
  }

  /**
   * `consent update` do gtag. Reklama to trzy osobne sygnały - Google wymaga
   * kompletu, samo `ad_storage` nie wystarcza od Consent Mode v2.
   */
  private push(state: ConsentState): void {
    const gtag = window.gtag;
    if (!gtag) {
      // tag nie wstał (bloker reklam, brak sieci) - nie ma komu przekazać zgody
      return;
    }

    const ads = state.marketing ? 'granted' : 'denied';
    gtag('consent', 'update', {
      ad_storage: ads,
      ad_user_data: ads,
      ad_personalization: ads,
      analytics_storage: state.analytics ? 'granted' : 'denied',
    });
  }

  private read(): StoredConsent | null {
    try {
      const raw = localStorage.getItem(ConsentService.STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as Partial<StoredConsent>;
      if (parsed?.version !== ConsentService.VERSION) {
        return null;
      }
      return {
        analytics: parsed.analytics === true,
        marketing: parsed.marketing === true,
        version: ConsentService.VERSION,
        date: parsed.date ?? '',
      };
    } catch {
      return null;
    }
  }
}
